var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getVirtualizedAppName } from "../src/View/Hooks/useVirtualizedApp";
import { getXSRF } from "../src/Services/Shared/FetchService";
class Fetcher {
    /**
     * **Fetch data from an endpoint, process single response**
     *
     * Here's what we mean by "*processing*" a single response:
     * * Single response is successful - **returned Promise resolves with data**
     * * Single response is unsuccessful - **returned Promise rejects with message**
     *
     * Note that the promise may reject for other reasons, and you should not assume that
     * it will always reject with the message from the single response.  It may make
     * sense to check if the rejected value is a string before trying to display it.
     *
     * @param {string} url - the url of the endpoint
     * @param {{[string]: any}} params - params to be passed in a query string to the endpoint.
     * @returns {Promise<T>} the data from the single response object.
     */
    static getSingleResponse(url, params = {}) {
        return (Fetcher.get(url, params)
            .catch((response) => {
            throw Fetcher.processSingleResponse(response);
        })
            // @ts-expect-error - TS2345 - Argument of type '<T>(singleResponse: SingleResponse<T>) => T' is not assignable to parameter of type '(value: unknown) => unknown'.
            .then(Fetcher.processSingleResponse));
    }
    /**
     * Fetch data wrapped in a PagedResponse using a get request.
     *
     * @param {string} url -  - the url of the endpoint
     * @param {[string]: any} params - params to be passed in a query string to the endpoint
     * @returns {Promise<PagedResult<T>} a paged result object with the data from the paged response.
     */
    static getPagedResponse(url, params = {}) {
        return (Fetcher.get(url, params)
            .catch((response) => {
            throw Fetcher.processPagedResponse(response);
        })
            // @ts-expect-error - TS2345 - Argument of type '<T>(pagedResponse: PagedResponse<T>) => PagedResult<T>' is not assignable to parameter of type '(value: unknown) => PagedResult<unknown> | PromiseLike<PagedResult<unknown>>'.
            .then(Fetcher.processPagedResponse));
    }
    /**
     * Fetch data using a get request.
     *
     * @param {string} url - the url of the endpoint
     * @param {{[string]: any}} params - params to be passed in a query string to the endpoint.
     * @returns {T} the data from the single response object.
     */
    static get(url, params = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const virtualizedAppName = getVirtualizedAppName();
            const response = yield fetch(url + Fetcher.toQueryString(params), {
                credentials: "include",
                headers: Object.assign({ "X-Requested-With": "XMLHttpRequest", Accept: "application/json" }, (virtualizedAppName
                    ? { VirtualizedAppName: virtualizedAppName.toString() }
                    : {})),
                method: "GET",
            });
            let data;
            try {
                data = yield response.json();
            }
            catch (error) {
                if (console.error) {
                    console.error("Could not parse json from fetch", response);
                }
                throw error;
            }
            if (!response.ok) {
                throw data;
            }
            return data;
        });
    }
    /**
     * **Post data to a endpoint using a post and process the returned single response**
     *
     * Here's what we mean by "*processing*" a single response:
     * * Single response is successful - **returned Promise resolves with data**
     * * Single response is unsuccessful - **returned Promise rejects with message**
     *
     * Note that the promise may reject for other reasons, and you should not assume that
     * it will always reject with the message from the single response.  It may make
     * sense to check if the rejected value is a string before trying to display it.
     *
     * @param {string} url - the url of the endpoing
     * @param {any} body - the data to send to the endpoing
     * @returns {Promise<T>} A promise which resolves with the data from the single response.
     */
    static postSingleResponse(url, body) {
        return __awaiter(this, void 0, void 0, function* () {
            return (Fetcher.post(url, body)
                .catch((response) => {
                throw Fetcher.processSingleResponse(response);
            })
                // @ts-expect-error - TS2345 - Argument of type '<T>(singleResponse: SingleResponse<T>) => T' is not assignable to parameter of type '(value: unknown) => unknown'.
                .then(Fetcher.processSingleResponse));
        });
    }
    /**
     * Post data to an endpoint
     *
     * @param {string} url - the url of the endpoint
     * @param {any} body - data to be passed in the body of the request
     */
    static post(url, body) {
        return __awaiter(this, void 0, void 0, function* () {
            const virtualizedAppName = getVirtualizedAppName();
            const response = yield fetch(url, {
                credentials: "include",
                method: "POST",
                body: JSON.stringify(body),
                headers: Object.assign(Object.assign({}, (virtualizedAppName
                    ? { VirtualizedAppName: virtualizedAppName.toString() }
                    : {})), { Accept: "application/json", "X-XSRF-TOKEN": getXSRF() }),
            });
            let data;
            try {
                data = yield response.json();
            }
            catch (error) {
                if (console.error) {
                    console.error("Could not parse json from fetch", response);
                }
                throw error;
            }
            if (!response.ok) {
                throw data;
            }
            return data;
        });
    }
    /**
     * Returns the data from a single response and throws an error if success is equal to false.
     *
     * @param {SingleResponse<T>} singleResponse - the single response
     * @throws {Error} when the single response has success false
     * @returns {T} data from the single response
     */
    static processSingleResponse(singleResponse) {
        if (!singleResponse.success) {
            if (console.error) {
                console.error(singleResponse);
            }
            throw (singleResponse.message ||
                "Invalid single response: " + singleResponse.toString());
        }
        console.log("single response data is ", singleResponse.data);
        return singleResponse.data;
    }
    /**
     * Maps a PagedResponse to a PagedResult.
     *
     * PagedResult is a subset of PagedResponse.  This method is intended to be used
     * by the Fetcher service to process PagedResponses received from the server.
     *
     * @param {PagedResponse<T>} pagedResponse -- the paged response
     * @throws {Error} when the paged response has success false
     * @returns {PagedResult<T>} paged result with the data from the paged response
     */
    static processPagedResponse(pagedResponse) {
        if (!pagedResponse.success) {
            if (console.error) {
                console.error(pagedResponse);
            }
            throw (pagedResponse.message ||
                "Invalid paged response: " + pagedResponse.toString());
        }
        return {
            data: pagedResponse.data,
            total: pagedResponse.total,
            offset: pagedResponse.offset,
            maxResults: pagedResponse.maxResults,
        };
    }
}
Fetcher.toQueryString = (params) => {
    // @ts-expect-error - TS7034 - Variable 'definedParams' implicitly has type 'any' in some locations where its type cannot be determined.
    let definedParams;
    if (params && Object.keys(params).length) {
        // Defensive copy so flow type refinement isn't invalidated.
        definedParams = params;
    }
    else {
        return "";
    }
    return ("?" +
        Object.keys(definedParams)
            // @ts-expect-error - TS7005 - Variable 'definedParams' implicitly has an 'any' type.
            .filter((key) => definedParams[key] != null)
            .map((key) => {
            // @ts-expect-error - TS7005 - Variable 'definedParams' implicitly has an 'any' type.
            if (Array.isArray(definedParams[key])) {
                // @ts-expect-error - TS7005 - Variable 'definedParams' implicitly has an 'any' type.
                return definedParams[key]
                    .map(
                // @ts-expect-error - TS7006 - Parameter 'val' implicitly has an 'any' type.
                (val) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
                    .join("&");
            }
            return `${encodeURIComponent(key)}=${encodeURIComponent(
            // @ts-expect-error - TS7005 - Variable 'definedParams' implicitly has an 'any' type.
            definedParams[key])}`;
        })
            .join("&"));
};
export default Fetcher;
